import type { Edge } from '@metro/graph'
import { EdgeType } from '@metro/graph'
import type { Leg, Route, Stop } from './route'
import { recomputeID } from './route'

export function edgesToRoute(edges: readonly Edge[]): Route {
  const legs: Leg[] = []
  let totalTime = 0
  let walkingTime = 0
  let transferCount = 0
  let offset = 0
  let currentRideLine: string | null = null
  let currentRideStops: Stop[] = []
  let currentRideDuration = 0

  const flushRide = (): void => {
    if (currentRideLine === null) return
    legs.push({
      type: 'ride',
      line_id: currentRideLine,
      stops: currentRideStops,
      duration_s: currentRideDuration,
    })
    currentRideLine = null
    currentRideStops = []
    currentRideDuration = 0
  }

  // Whether a Ride edge exists strictly AFTER each index — a street
  // walk counts as a transfer only strictly BETWEEN two rides (canon
  // 2026-07): leading/trailing street walks are terminal approaches,
  // not interchanges. In-station transfer counting is unchanged.
  const hasRideAfter = new Array<boolean>(edges.length)
  {
    let seenRide = false
    for (let i = edges.length - 1; i >= 0; i--) {
      hasRideAfter[i] = seenRide
      if (edges[i].type === EdgeType.Ride) seenRide = true
    }
  }

  for (let i = 0; i < edges.length; i++) {
    const e = edges[i]
    totalTime += e.weight
    if (e.type === EdgeType.Ride) {
      if (currentRideLine === null || currentRideLine !== e.from.lineID) {
        flushRide()
        currentRideLine = e.from.lineID
        currentRideStops = [
          { stationId: e.from.id, naptanId: e.from.naptanId, arrival_offset_s: offset },
        ]
      }
      offset += e.weight
      currentRideStops.push({
        stationId: e.to.id,
        naptanId: e.to.naptanId,
        arrival_offset_s: offset,
      })
      currentRideDuration += e.weight
    } else if (e.type === EdgeType.WithinStationTransfer || e.type === EdgeType.WalkTransfer) {
      flushRide()
      const isWalk = e.type === EdgeType.WalkTransfer
      if (isWalk) walkingTime += e.weight
      // Street walk edges (schema transition `street: true`, 1.27.0+)
      // emit the 2026-07 street-walk wire shape: EMPTY transition_id +
      // both station ids. Stamping the schema transition id here would
      // make presentation misclassify the leg as an in-station hop
      // (fold into "Make a transfer", skip the map pictogram) because
      // the id RESOLVES in the schema.
      const isStreet = isWalk && e.street === true
      legs.push({
        type: isWalk ? 'walk' : 'transfer',
        transition_id: isStreet ? '' : e.realID,
        from_station_id: e.from.id,
        to_station_id: e.to.id,
        duration_s: e.weight,
      })
      offset += e.weight
      // Increment transferCount only when this leg follows a RIDE.
      // Consecutive transfer / walk legs within one physical hub
      // (canonical case: Paddington's 4-platform indoor walk
      // h&c-PAC → cir-PAC1 → eliz-PAC → cir-PAC0 → bak-PAC, which
      // realises ONE interchange via four schema-baked transition
      // pills) collapse into a single perceived interchange — the
      // user crossed lines once, not four times. Without this fix
      // such routes count as 4 transfers, lose Pareto to the walking
      // direct route, and never surface; the offline router then
      // falls back to absurd detours via Willesden Junction or
      // Shepherd's Bush to dodge the chain.
      const prevIdx = legs.length - 2
      const prevWasRide = prevIdx >= 0 && legs[prevIdx].type === 'ride'
      // Street walks count only strictly BETWEEN two rides; a trailing
      // street walk (no ride after) is a terminal approach (canon
      // 2026-07). In-station legs keep the pre-street behaviour.
      const countable = !isStreet || hasRideAfter[i]
      if (prevWasRide && countable) transferCount += 1
    }
  }
  flushRide()

  const r: Route = {
    id: '',
    total_time_s: totalTime,
    transfer_count: transferCount,
    walking_time_s: walkingTime,
    legs,
  }
  return recomputeID(r)
}
